import { http } from '../lib/api/http';

const BASE_PATH = '/service-requests';

const DEFAULT_LIMIT = 100;

const DEFAULT_REQUEST_CURRENCY = 'MXN';

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

const extractItems = payload => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.items)) {
    return payload.items;
  }
  return [];
};

const normalizeListMeta = (payload, { page = 1, limit = DEFAULT_LIMIT, items = [] } = {}) => {
  const totalItems = Number(payload?.total ?? payload?.totalItems ?? items.length);
  const rawTotalPages = Number(payload?.total_pages ?? payload?.totalPages);
  const totalPages =
    Number.isFinite(rawTotalPages) && rawTotalPages > 0
      ? rawTotalPages
      : limit > 0
      ? Math.max(1, Math.ceil(totalItems / limit))
      : 1;
  const rawPage = Number(payload?.page);
  const currentPage = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : page;
  const hasMoreFlag =
    payload && (payload.has_more != null || payload.hasMore != null)
      ? Boolean(payload.has_more ?? payload.hasMore)
      : currentPage < totalPages;

  return {
    page: currentPage,
    totalPages,
    totalItems,
    hasMore: hasMoreFlag,
  };
};

const normalizeISODate = value => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};

const normalizeCurrencyCode = value => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim().toUpperCase();
  }
  return '';
};

const buildCoordinatePair = (latitudeLike, longitudeLike) => {
  const latitudeCandidate = Number(latitudeLike);
  const longitudeCandidate = Number(longitudeLike);
  const isLatValid = Number.isFinite(latitudeCandidate);
  const isLngValid = Number.isFinite(longitudeCandidate);
  if (!isLatValid || !isLngValid) {
    return null;
  }

  const latWithinRange = Math.abs(latitudeCandidate) <= 90;
  const lngWithinRange = Math.abs(longitudeCandidate) <= 180;

  if (latWithinRange && lngWithinRange) {
    return { latitude: latitudeCandidate, longitude: longitudeCandidate };
  }

  if (Math.abs(longitudeCandidate) <= 90 && Math.abs(latitudeCandidate) <= 180) {
    return { latitude: longitudeCandidate, longitude: latitudeCandidate };
  }

  return null;
};

const parseCoordinateValue = value => {
  if (value == null || value === '') {
    return null;
  }

  if (Array.isArray(value)) {
    if (value.length < 2) {
      return null;
    }
    return buildCoordinatePair(value[0], value[1]);
  }

  if (typeof value === 'object') {
    if (value.type === 'Point' && Array.isArray(value.coordinates) && value.coordinates.length >= 2) {
      return buildCoordinatePair(value.coordinates[1], value.coordinates[0]);
    }

    const candidate = buildCoordinatePair(
      value.latitude ?? value.lat ?? value.y ?? value[1],
      value.longitude ?? value.lng ?? value.lon ?? value.x ?? value[0]
    );
    if (candidate) {
      return candidate;
    }

    if (Array.isArray(value.coordinates)) {
      return parseCoordinateValue(value.coordinates);
    }
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = JSON.parse(trimmed);
      const candidate = parseCoordinateValue(parsed);
      if (candidate) {
        return candidate;
      }
    } catch {
      // ignore json parse errors
    }

    const matches = trimmed.match(/-?\d+(?:\.\d+)?/g);
    if (matches && matches.length >= 2) {
      return buildCoordinatePair(matches[0], matches[1]);
    }
  }

  return null;
};

const pickCoordinatesFromRecord = (record, compositeKeys = [], latLngPairs = []) => {
  if (!record) {
    return null;
  }

  for (const key of compositeKeys) {
    if (record[key] == null) {
      continue;
    }
    const parsed = parseCoordinateValue(record[key]);
    if (parsed) {
      return parsed;
    }
  }

  for (const [latKey, lngKey] of latLngPairs) {
    if (record[latKey] == null || record[lngKey] == null) {
      continue;
    }
    const parsed = parseCoordinateValue([record[latKey], record[lngKey]]);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const PICKUP_COORDINATE_KEYS = [
  'pickupCoordinates',
  'pickup_coordinates',
  'pickupCoords',
  'pickup_coords',
  'originCoordinates',
  'origin_coordinates',
  'originCoords',
  'origin_coords',
  'pickupLocationCoordinates',
  'pickup_location_coordinates',
  'originLocationCoordinates',
  'origin_location_coordinates',
];

const DROPOFF_COORDINATE_KEYS = [
  'dropoffCoordinates',
  'dropoff_coordinates',
  'dropoffCoords',
  'dropoff_coords',
  'destinationCoordinates',
  'destination_coordinates',
  'destinationCoords',
  'destination_coords',
  'dropoffLocationCoordinates',
  'dropoff_location_coordinates',
  'destinationLocationCoordinates',
  'destination_location_coordinates',
];

const PICKUP_LAT_LNG_FIELDS = [
  ['pickupLatitude', 'pickupLongitude'],
  ['pickup_latitude', 'pickup_longitude'],
  ['originLatitude', 'originLongitude'],
  ['origin_latitude', 'origin_longitude'],
];

const DROPOFF_LAT_LNG_FIELDS = [
  ['dropoffLatitude', 'dropoffLongitude'],
  ['dropoff_latitude', 'dropoff_longitude'],
  ['destinationLatitude', 'destinationLongitude'],
  ['destination_latitude', 'destination_longitude'],
];

const normalizeSelectValue = value => {
  if (Array.isArray(value)) {
    return value.find(Boolean) || '';
  }
  if (value === null || value === undefined) return '';
  return value;
};

const mapCompany = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      category: '',
      logoUrl: '',
      slug: '',
    };
  }

  const companyRecord = Array.isArray(record) ? record[0] : record;
  const categories = Array.isArray(companyRecord?.categories)
    ? companyRecord.categories
    : [companyRecord?.category].filter(Boolean);

  return {
    id: companyRecord?.id || '',
    name: companyRecord?.name || '',
    category: normalizeSelectValue(categories),
    logoUrl: companyRecord?.logo_url || companyRecord?.logoUrl || '',
    slug: companyRecord?.slug || '',
  };
};

const mapCreator = record => {
  if (!record) {
    return {
      id: '',
      name: '',
      email: '',
      username: '',
      avatarUrl: '',
    };
  }

  const creatorRecord = Array.isArray(record) ? record[0] : record;
  return {
    id: creatorRecord?.id || '',
    name:
      creatorRecord?.name ||
      creatorRecord?.display_name ||
      creatorRecord?.displayName ||
      creatorRecord?.username ||
      creatorRecord?.email ||
      '',
    email: creatorRecord?.email || '',
    username: creatorRecord?.username || '',
    avatarUrl: creatorRecord?.avatar_url || creatorRecord?.avatarUrl || '',
  };
};

const mapRecord = record => {
  if (!record || typeof record !== 'object') return null;

  const companySource =
    typeof record.company === 'object'
      ? record.company
      : record.expand?.company;
  const creatorSource =
    typeof record.createdBy === 'object'
      ? record.createdBy
      : record.creator || record.expand?.createdBy;

  const company = mapCompany(companySource);
  const creator = mapCreator(creatorSource);
  const pickupCoordinates = pickCoordinatesFromRecord(
    record,
    PICKUP_COORDINATE_KEYS,
    PICKUP_LAT_LNG_FIELDS
  );
  const dropoffCoordinates = pickCoordinatesFromRecord(
    record,
    DROPOFF_COORDINATE_KEYS,
    DROPOFF_LAT_LNG_FIELDS
  );

  const companyId =
    record.company_id ||
    record.companyId ||
    (typeof record.company === 'string' ? record.company : '') ||
    company.id;

  const createdBy =
    record.created_by ||
    record.createdBy ||
    (typeof record.creator_id === 'string' ? record.creator_id : '') ||
    creator.id;

  return {
    id: record.id,
    title: record.title || '',
    serviceType: record.service_type || record.serviceType || 'transport',
    status: record.status || 'draft',
    pickupLocation: record.pickup_location || record.pickupLocation || '',
    dropoffLocation: record.dropoff_location || record.dropoffLocation || '',
    neededBy: record.needed_by || record.neededBy || '',
    budget: record.budget || '',
    currency: normalizeCurrencyCode(record.currency) || DEFAULT_REQUEST_CURRENCY,
    details: record.details || '',
    companyId,
    createdBy,
    created: record.created_at || record.created || record.createdAt,
    updated: record.updated_at || record.updated || record.updatedAt,
    company,
    creator,
    pickupCoordinates,
    dropoffCoordinates,
  };
};

const buildCreatePayload = (payload = {}) => {
  const body = {
    title: payload.title?.trim() || '',
    service_type: payload.serviceType || 'transport',
    status: payload.status || 'draft',
    pickup_location: payload.pickupLocation?.trim() || '',
    dropoff_location: payload.dropoffLocation?.trim() || '',
    budget: payload.budget?.trim() || '',
    details: payload.details?.trim() || '',
    currency: normalizeCurrencyCode(payload.currency) || DEFAULT_REQUEST_CURRENCY,
  };

  const neededByISO = normalizeISODate(payload.neededBy);
  if (neededByISO) {
    body.needed_by = neededByISO;
  }

  if (payload.company || payload.companyId || payload.company_id) {
    body.company_id = payload.company || payload.companyId || payload.company_id;
  }

  return body;
};

const buildUpdatePayload = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const body = {};

  if (hasOwn(payload, 'title')) {
    body.title = payload.title?.trim() || '';
  }
  if (hasOwn(payload, 'serviceType')) {
    body.service_type = payload.serviceType || '';
  }
  if (hasOwn(payload, 'status')) {
    body.status = payload.status || '';
  }
  if (hasOwn(payload, 'pickupLocation')) {
    body.pickup_location = payload.pickupLocation?.trim() || '';
  }
  if (hasOwn(payload, 'dropoffLocation')) {
    body.dropoff_location = payload.dropoffLocation?.trim() || '';
  }
  if (hasOwn(payload, 'neededBy')) {
    body.needed_by = normalizeISODate(payload.neededBy);
  }
  if (hasOwn(payload, 'budget')) {
    body.budget = payload.budget?.trim() || '';
  }
  if (hasOwn(payload, 'details')) {
    body.details = payload.details?.trim() || '';
  }
  if (hasOwn(payload, 'currency')) {
    body.currency = normalizeCurrencyCode(payload.currency) || DEFAULT_REQUEST_CURRENCY;
  }
  if (hasOwn(payload, 'company') || hasOwn(payload, 'companyId') || hasOwn(payload, 'company_id')) {
    body.company_id = payload.company || payload.companyId || payload.company_id || '';
  }

  return body;
};

export async function listRequests({
  companyId,
  createdBy,
  status,
  limit = DEFAULT_LIMIT,
  page = 1,
} = {}) {
  const params = {
    limit,
    page,
  };

  if (companyId) {
    params.company_id = companyId;
  }
  if (createdBy) {
    params.created_by = createdBy;
  }
  if (Array.isArray(status) && status.length) {
    params.status = status;
  } else if (status) {
    params.status = [status];
  }

  const response = await http.get(BASE_PATH, { params });
  const payload = response.data;
  const items = extractItems(payload).map(mapRecord).filter(Boolean);
  return items;
}

export async function listPublishedRequests({
  limit = 5,
  statuses = ['published', 'in_progress'],
  page = 1,
  withMeta = false,
} = {}) {
  const params = {
    limit,
    page,
    with_meta: withMeta,
  };

  if (Array.isArray(statuses) && statuses.length) {
    params.status = statuses;
  } else if (typeof statuses === 'string' && statuses.trim()) {
    params.status = [statuses.trim()];
  }

  const response = await http.get(BASE_PATH, { params });
  const payload = response.data;
  const items = extractItems(payload).map(mapRecord).filter(Boolean);

  if (withMeta) {
    const meta = normalizeListMeta(payload, { page, limit, items });
    return {
      items,
      page: meta.page,
      totalPages: meta.totalPages,
      totalItems: meta.totalItems,
      hasMore: meta.hasMore,
    };
  }

  return items;
}

export async function getRequestById(id) {
  if (!id) {
    throw new Error('El identificador de la solicitud es requerido.');
  }

  const response = await http.get(`${BASE_PATH}/${id}`);
  return mapRecord(response.data);
}

export async function createRequest(payload) {
  const body = buildCreatePayload(payload);
  const response = await http.post(BASE_PATH, body);
  return mapRecord(response.data);
}

export async function updateRequest(id, payload) {
  if (!id) {
    throw new Error('El identificador de la solicitud es requerido.');
  }
  const body = buildUpdatePayload(payload);
  const response = await http.patch(`${BASE_PATH}/${id}`, body);
  return mapRecord(response.data);
}

export async function deleteRequest(id) {
  if (!id) return false;
  await http.delete(`${BASE_PATH}/${id}`);
  return true;
}

export async function setRequestStatus(id, nextStatus) {
  if (!id) {
    throw new Error('El identificador de la solicitud es requerido.');
  }
  const response = await http.patch(`${BASE_PATH}/${id}`, { status: nextStatus });
  return mapRecord(response.data);
}

export const REQUEST_SERVICE_TYPES = [
  { value: 'transport', label: 'Transporte' },
  { value: 'custodia', label: 'Custodia' },
  { value: 'logistica', label: 'Logistica' },
];

export const REQUEST_STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'published', label: 'Publicado' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];
